import { REFUSAL_CONCERNS, readRemedy } from "../access/decisions";
import { enumMembers, type ApiSchema } from "../apiSchema";
import type { RouteUnavailability } from "../connectorStatus";
import { readArray, readInteger, readNullableString, readObject, readOneOf, readString } from "../http/jsonDecoding";
import {
  readThreadEntry,
  readThreadFacts,
  readThreadListing,
  type StepDisplayState,
  type ThreadBindings,
  type ThreadDetail,
  type ThreadEntry,
  type ThreadFacts,
  type ThreadListing,
  type ThreadProjection,
  type ThreadSettings,
  type ThreadViewer,
  type ViewerAction,
  type ViewerActionName,
} from "./liveMessages";

/**
 * The Thread routes' response bodies (`server/src/dmw/api/routes/threads/bodies.py`), checked
 * and turned into the web app's names. Settings, bindings and entries are module C's own
 * models and are checked against the live schema (`liveMessages.ts`).
 */

export type ThreadState = ApiSchema<"ThreadState">;
export type TurnState = ApiSchema<"TurnState">;
export type TurnKind = ApiSchema<"TurnKind">;
export type TurnExecutor = ApiSchema<"TurnExecutor">;
export type StepProgressState = ApiSchema<"StepProgressState">;
export type HostOfferState = ApiSchema<"HostOfferState">;

export const THREAD_STATES = enumMembers<ThreadState>({ saved: true, live: true, closed: true });
export const TURN_STATES = enumMembers<TurnState>({
  queued: true,
  dispatched: true,
  running: true,
  cancelling: true,
  completed: true,
  failed: true,
  cancelled: true,
});
export const TURN_EXECUTORS = enumMembers<TurnExecutor>({ agent: true, tool: true });
export const STEP_PROGRESS_STATES = enumMembers<StepProgressState>({
  not_due: true,
  due: true,
  in_progress: true,
  done: true,
  failed: true,
  skipped: true,
});
const VIEWER_ACTIONS = enumMembers<ViewerActionName>({
  post_message: true,
  request_reply: true,
  cancel_turn: true,
  answer_request: true,
  run_step: true,
  skip_step: true,
  run_to: true,
  stop_run_to: true,
  reopen: true,
  offer_hosting: true,
  request_hosting: true,
  attach: true,
  move_hosting: true,
  accept_hosting: true,
  approve_hosting: true,
  rename: true,
  change_settings: true,
  leave_workflow: true,
  resume_workflow: true,
  switch_workflow: true,
  run_on_many: true,
  fork: true,
  save_as_workflow: true,
  remove: true,
});
/** Module C's refusals of a Run action, and module A's for a permission the viewer lacks. */
type ViewerRefusal = NonNullable<ViewerAction["refusal"]>;
const VIEWER_REFUSALS = enumMembers<ViewerRefusal>({
  closed: true,
  saved_not_host: true,
  not_host: true,
  not_own_route: true,
  turn_open: true,
  request_waiting: true,
  already_host: true,
  no_active_workflow: true,
  step_not_skippable: true,
  step_already_ran: true,
  missing_permission: true,
});
const HOST_OFFER_STATES = enumMembers<HostOfferState>({
  pending: true,
  requested: true,
  accepted: true,
  declined: true,
  withdrawn: true,
  expired: true,
});

/** A switch away from a Workflow (F126), as its entry recorded it. */
export interface Departure {
  /** The Workflow version left. */
  readonly workflow: string;
  /** Its furthest step that was done or skipped; `null` when none was. */
  readonly afterStep: string | null;
}

/**
 * `GET /threads/{id}` in the web app's names: what never changes about the Thread, and what it
 * says now (`facts`, W-22). The live state carries the same facts; `withFacts` puts the newer
 * of the two in (`useThreadOverview`), so nothing here is merged from two reads.
 */
export interface ThreadOverview {
  readonly threadId: string;
  readonly projectId: string;
  /** The fork point `<thread id>#<number>`, or `null` for a started Thread. */
  readonly parent: string | null;
  readonly firstOwnNumber: number;
  /** Set when the Thread is a saved example run imported from a recording, not run here. */
  readonly importedExample: ImportedExample | null;
  readonly title: string;
  readonly state: ThreadState;
  /** User ID. */
  readonly host: string;
  readonly attachedConnector: string | null;
  readonly connectorOnline: boolean;
  /** Why the attached connector can't run its replies now, as module C decides it for dispatch (CP-11); `null` when it can. */
  readonly routeUnavailableBecause: RouteUnavailability | null;
  /** The Workflow version it follows (`projection.workflow`), or `null` for a free Thread. */
  readonly workflow: string | null;
  /** Each switch away from a Workflow, in order, as its entry recorded it (F126): what the Thread followed before. */
  readonly departures: readonly Departure[];
  readonly settings: ThreadSettings;
  readonly bindings: ThreadBindings;
  /** The attached connector also runs other participants' turns (a server host); a PC's runs only its host's (R2). */
  readonly servesParticipants: boolean;
  /** Messages, by entry number, that wait until their sender holds the Thread and runs them on their own agent (T-6, R2). */
  readonly waitingForSender: readonly number[];
  /** Steps the Thread kept at its fork that read an item with a newer version now, until they run again. */
  readonly keptInputNotes: readonly KeptInputNote[];
  /** The entry its facts reflect (`projection.through_entry`): what a command names as the last it saw. */
  readonly lastEntryNumber: number;
  /** Its status, as module C decides it (W-22). */
  readonly projection: ThreadProjection;
  /** Its open turn, tool runs and steps, from the same projection. */
  readonly detail: ThreadDetail;
  /** What the viewer may do now, as module C decides it for them (W-22 slice 2); from the same read as the facts. */
  readonly viewer: ThreadViewer;
}

/** Each step's progress by step key, in the Workflow's order, as module C folds it (T-7). */
export function stepProgressOf(detail: ThreadDetail): Readonly<Record<string, StepProgressState>> {
  return Object.fromEntries((detail.steps ?? []).map((step) => [step.step_key, step.progress] as const));
}

/** Each step's state as a person sees it, by step key, as module C shows it (W-22): what the Run view and the graph name. */
export function stepDisplayStatesOf(detail: ThreadDetail): Readonly<Record<string, StepDisplayState>> {
  return Object.fromEntries((detail.steps ?? []).map((step) => [step.step_key, step.display_state] as const));
}

/** What never changes about a Thread: the overview without its facts. */
export type ThreadIdentity = Pick<ThreadOverview, "threadId" | "projectId" | "parent" | "firstOwnNumber" | "importedExample">;

/** A kept step read an item that has a newer version now: a quiet note, never a reason to rerun it. */
export interface KeptInputNote {
  readonly stepKey: string;
  /** The placeholder or tool input that read it. */
  readonly input: string;
  readonly read: string;
  readonly now: string;
}

export function readKeptInputNotes(value: unknown): readonly KeptInputNote[] {
  return readArray(value, "kept input changes", (item) => {
    const note = readObject(item, "kept input change");
    return { stepKey: readString(note, "step_key"), input: readString(note, "input"), read: readString(note, "read"), now: readString(note, "now") };
  });
}

/** When and with what an imported example Thread was recorded (Thread import). */
export interface ImportedExample {
  readonly recordedAt: string;
  readonly model: string;
  readonly connectorVersion: string;
}

/**
 * One line of the Thread list (`GET /projects/{p}/threads`, `GET /threads/{id}/forks`, and
 * the project feed of the live socket, Q-61, Q-62): enough for its status and unread marker
 * without reading the Thread.
 */
export interface ThreadSummary {
  readonly attachedConnector: string | null;
  readonly subject?: string | null;
  /** What an "Edit an item" Thread edits, as its edit step names it (`editedItem.ts`); its line is filed under that item (ADR-0031 D2). */
  readonly editedItem?: string | null;
  readonly threadId: string;
  readonly projectId: string;
  readonly title: string;
  readonly parent: string | null;
  readonly state: ThreadState;
  readonly host: string;
  readonly lastEntryNumber: number;
  /** ISO 8601. */
  readonly lastEntryAt: string;
  /** Its status, the same projection its overview and live state carry (W-22). */
  readonly projection: ThreadProjection;
  /** ISO 8601: when the Thread was started or forked; the list's order counts it (F44, as T3 Code). */
  readonly createdAt: string;
  /** ISO 8601, or `null` before the first: the newest turn requested and finished, and the newest thing a person wrote. */
  readonly latestTurnRequestedAt: string | null;
  readonly latestTurnFinishedAt: string | null;
  readonly latestPersonWrittenAt: string | null;
  /** Neither working nor waiting for a person's input: module C accepts a settle or snooze (F44). */
  readonly canSettleOrSnooze: boolean;
}

export interface EntriesPage {
  readonly entries: readonly ThreadEntry[];
  /** The path's last number now. */
  readonly lastEntryNumber: number;
}

export interface TurnSummary {
  readonly turnId: string;
  readonly state: TurnState;
}

export interface HostOffer {
  readonly offerId: string;
  readonly threadId: string;
  readonly fromUser: string;
  readonly toUser: string;
  readonly state: HostOfferState;
  readonly expiresAt: string;
}

/**
 * What waits for the signed-in user to decide, with what it is about (Q-60): an offer of the
 * host role made to them (`pending`), or a request for their role (`requested`, F55).
 */
export interface IncomingHostOffer extends HostOffer {
  readonly projectId: string;
  readonly threadTitle: string;
  /** Who gives the role: the host. */
  readonly fromDisplayName: string;
  /** Who takes it: for a request, who asks. */
  readonly toDisplayName: string;
}

export interface ThreadSearchHit {
  /** `<thread id>#<number>`, or `null` when the title matched. */
  readonly entry: string | null;
  readonly threadId: string;
  readonly threadTitle: string;
  readonly authorUserId: string | null;
  readonly at: string;
  readonly snippet: string;
}

export function decodeThreadOverview(body: unknown): ThreadOverview {
  const thread = readObject(body, "Thread");
  const identity: ThreadIdentity = {
    threadId: readString(thread, "thread_id"),
    projectId: readString(thread, "project_id"),
    parent: readNullableString(thread, "parent"),
    firstOwnNumber: readInteger(thread, "first_own_number"),
    importedExample: decodeImportedExample(thread["imported_example"]),
  };
  return withFacts(identity, readThreadFacts(thread["facts"]), readThreadViewer(thread["viewer"]));
}

function readThreadViewer(value: unknown): ThreadViewer {
  const viewer = readObject(value, "viewer");
  return {
    actions: readArray(viewer["actions"] ?? [], "viewer actions", (item): ViewerAction => {
      const action = readObject(item, "viewer action");
      const remedy = readRemedy(action["remedy"]);
      return {
        action: readOneOf(action, "action", VIEWER_ACTIONS),
        target: action["target"] === undefined ? null : readNullableString(action, "target"),
        refusal: action["refusal"] == null ? null : readOneOf(action, "refusal", VIEWER_REFUSALS),
        concern: action["concern"] == null ? null : readOneOf(action, "concern", REFUSAL_CONCERNS),
        remedy: remedy === null ? null : { needs: remedy.needs, holders: [...remedy.holders], deciders: [...remedy.deciders], can_request: remedy.canRequest },
      };
    }),
  };
}

/** The overview with these facts and the viewer's section of the same read: the overview read's or the live state's. */
export function withFacts(identity: ThreadIdentity, facts: ThreadFacts, viewer: ThreadViewer): ThreadOverview {
  return {
    threadId: identity.threadId,
    projectId: identity.projectId,
    parent: identity.parent,
    firstOwnNumber: identity.firstOwnNumber,
    importedExample: identity.importedExample,
    title: facts.title,
    state: facts.state,
    host: facts.host,
    attachedConnector: facts.attached_connector,
    connectorOnline: facts.connector_online,
    routeUnavailableBecause: facts.route_unavailable_because,
    workflow:facts.projection.workflow?.reference ?? null,
    departures: (facts.departures ?? []).map((departure) => ({ workflow: departure.workflow, afterStep: departure.after_step })),
    settings: facts.settings,
    bindings: facts.bindings,
    servesParticipants: facts.serves_participants,
    waitingForSender: facts.waiting_for_sender ?? [],
    keptInputNotes: (facts.kept_input_changes ?? []).map((note) => ({ stepKey: note.step_key, input: note.input, read: note.read, now: note.now })),
    lastEntryNumber: facts.projection.through_entry,
    projection: facts.projection,
    detail: facts.detail,
    viewer,
  };
}

function decodeImportedExample(value: unknown): ImportedExample | null {
  if (value === undefined || value === null) return null;
  const example = readObject(value, "imported example");
  return {
    recordedAt: readString(example, "recorded_at"),
    model: readString(example, "model"),
    connectorVersion: readString(example, "connector_version"),
  };
}

export function decodeThreadSummaries(body: unknown): readonly ThreadSummary[] {
  return readArray(body, "Threads", (item) => threadSummaryOf(readThreadListing(item)));
}

/** The web app's names for a list line, whether it came over HTTP or the live socket. */
export function threadSummaryOf(listing: ThreadListing): ThreadSummary {
  return {
    threadId: listing.thread_id,
    projectId: listing.project_id,
    title: listing.title,
    subject: listing.subject ?? null,
    editedItem: listing.edited_item ?? null,
    parent: listing.parent,
    state: listing.state,
    host: listing.host,
    attachedConnector: listing.attached_connector ?? null,
    lastEntryNumber: listing.last_entry_number,
    lastEntryAt: listing.last_entry_at,
    projection: listing.projection,
    canSettleOrSnooze: listing.can_settle_or_snooze,
    createdAt: listing.created_at,
    latestTurnRequestedAt: listing.latest_turn_requested_at ?? null,
    latestTurnFinishedAt: listing.latest_turn_finished_at ?? null,
    latestPersonWrittenAt: listing.latest_person_written_at ?? null,
  };
}

export function decodeEntriesPage(body: unknown): EntriesPage {
  const page = readObject(body, "entries page");
  return {
    entries: readArray(page["entries"], "entries", readThreadEntry),
    lastEntryNumber: readInteger(page, "last_entry_number"),
  };
}

export function decodeTurnSummary(body: unknown): TurnSummary {
  const turn = readObject(body, "turn");
  return { turnId: readString(turn, "turn_id"), state: readOneOf(turn, "state", TURN_STATES) };
}

export function decodeHostOffer(body: unknown): HostOffer {
  const offer = readObject(body, "host offer");
  return {
    offerId: readString(offer, "offer_id"),
    threadId: readString(offer, "thread_id"),
    fromUser: readString(offer, "from_user"),
    toUser: readString(offer, "to_user"),
    state: readOneOf(offer, "state", HOST_OFFER_STATES),
    expiresAt: readString(offer, "expires_at"),
  };
}

export function decodeIncomingHostOffers(body: unknown): readonly IncomingHostOffer[] {
  return readArray(body, "host offers", (item) => {
    const offer = readObject(item, "host offer");
    return {
      ...decodeHostOffer(offer),
      projectId: readString(offer, "project_id"),
      threadTitle: readString(offer, "thread_title"),
      fromDisplayName: readString(offer, "from_display_name"),
      toDisplayName: readString(offer, "to_display_name"),
    };
  });
}

export function decodeHostOffers(body: unknown): readonly HostOffer[] {
  return readArray(body, "host offers", decodeHostOffer);
}

export function decodeSearchHits(body: unknown): readonly ThreadSearchHit[] {
  return readArray(body, "search hits", (item) => {
    const hit = readObject(item, "search hit");
    return {
      entry: readNullableString(hit, "entry"),
      threadId: readString(hit, "thread_id"),
      threadTitle: readString(hit, "thread_title"),
      authorUserId: readNullableString(hit, "author_user_id"),
      at: readString(hit, "at"),
      snippet: readString(hit, "snippet"),
    };
  });
}

